/**
 * The desk table: one row per watched site.
 *
 * Read left to right a row answers the questions in the order they are asked:
 * which site and whose, is it up, how has it been this month, how quickly does
 * it answer, when did we last look, and is anything about to run out.
 *
 * A site on our own platform wears a small mark beside its client (item K):
 * its page has the platform card where an outside site has the WordPress one.
 *
 * The site name is a stretched link (the row opens the site's page), and the
 * two controls that must not open it - the external link and the menu - are
 * lifted above it, the same arrangement as the scanner's lead rows.
 */
import { Link } from "react-router-dom";
import { Activity, ExternalLink, Globe, Lock } from "lucide-react";
import {
  expiryLevel,
  expiryTone,
  formatMs,
  formatUptime,
  shownUptime,
  stateKey,
  stateTone,
  type ExpiryKind,
  type MonitorExpiryView,
  type MonitorRow,
} from "../../../lib/monitoring";
import { EmptyState, StatusPill, fmtDateTime, fmtStamp, listCard } from "../../ui";
import { MonitorRowMenu } from "./MonitorRowMenu";
import { expiryText, stateLine, type T } from "./words";

/**
 * Site, state, uptime, response, last check, expiry, menu.
 *
 * The certificate and the domain share one column, one line each. Two columns
 * of "in 64 days" pushed the table past a 1280px laptop's content width for a
 * fact that is fine on almost every row; stacked, the rare warning still wears
 * its pill and the row stays readable without scrolling sideways.
 */
const COLS = "minmax(180px, 2fr) minmax(150px, 1.2fr) 88px 96px 88px minmax(170px, 1.3fr) 44px";

function ExpiryLine({ view, kind, t }: { view: MonitorExpiryView | null; kind: ExpiryKind; t: T }) {
  const Icon = kind === "cert" ? Lock : Globe;
  const label = t(`monitoring.${kind}.title`);
  // No row: an http site, or one the daily look has not reached yet. Nothing to
  // say in a table cell either way; the site page tells the two apart.
  if (!view) return null;
  const level = expiryLevel(view, kind);
  const text = expiryText(t, view);
  return (
    <span className="flex min-w-0 items-center gap-1.5" title={`${label}: ${fmtDateTime(view.at)}`}>
      <Icon className="crm-mut size-3 shrink-0" aria-label={label} />
      {level === "ok" || level === "unknown" ? (
        <span className="crm-mut crm-tnum crm-trunc text-[12.5px]">{text}</span>
      ) : (
        <StatusPill tone={expiryTone(level)}>{text}</StatusPill>
      )}
    </span>
  );
}

export function MonitorsTable({
  rows,
  canManage,
  engineOn,
  busyId,
  t,
  empty,
  onCheck,
  onPause,
  onEdit,
  onRemove,
}: {
  rows: MonitorRow[];
  canManage: boolean;
  engineOn: boolean;
  busyId: string | null;
  t: T;
  /** What the card says when nothing matches: a first-time desk and a search
   *  that found nothing need different sentences, so the caller picks. */
  empty: { title: string; body: string; action?: React.ReactNode };
  onCheck: (site: MonitorRow) => void;
  onPause: (site: MonitorRow, paused: boolean) => void;
  onEdit: (site: MonitorRow) => void;
  onRemove: (site: MonitorRow) => void;
}) {
  if (rows.length === 0) {
    return (
      <div className="crm-card">
        <EmptyState icon={Activity} title={empty.title} body={empty.body} action={empty.action} />
      </div>
    );
  }

  return (
    <div className="crm-card" style={listCard(COLS)}>
      <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 22 }}>
        <span>{t("monitoring.col.site")}</span>
        <span>{t("monitoring.col.state")}</span>
        <span>{t("monitoring.col.uptime")}</span>
        <span>{t("monitoring.col.response")}</span>
        <span>{t("monitoring.col.checked")}</span>
        <span>{t("monitoring.col.expiry")}</span>
        <span />
      </div>

      <div className="crm-rows pt-0">
        {rows.map((site) => {
          const said = stateLine(t, site);
          return (
            <div key={site.id} className="crm-list-row relative" style={{ gridTemplateColumns: COLS }}>
              <span className="min-w-0 pl-3">
                <Link
                  to={`/monitoring/${site.id}`}
                  className="crm-prow-n crm-trunc block max-w-full after:absolute after:inset-0 after:rounded-full"
                >
                  {site.label ?? site.host}
                </Link>
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="crm-prow-s crm-trunc">{site.clientName}</span>
                  {site.platformSiteId ? (
                    <StatusPill tone="info" className="shrink-0">
                      {t("monitoring.platform.badge")}
                    </StatusPill>
                  ) : null}
                  <a
                    href={site.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    title={site.url}
                    aria-label={t("monitoring.menu.visit")}
                    className="crm-mut relative z-[1] shrink-0 hover:text-[var(--color-primary)]"
                  >
                    <ExternalLink className="size-3" />
                  </a>
                </span>
              </span>

              <span className="flex min-w-0 flex-col items-start gap-0.5">
                <StatusPill tone={stateTone(site)}>{t(stateKey(site))}</StatusPill>
                {said ? (
                  <span className="crm-prow-s crm-trunc max-w-full" title={said}>
                    {said}
                  </span>
                ) : null}
              </span>

              <span className="crm-tnum text-[13px]">{formatUptime(shownUptime(site))}</span>
              <span className="crm-tnum text-[13px]">{formatMs(site.p50Ms7d)}</span>
              <span className="crm-mut crm-tnum text-[12.5px]" title={fmtDateTime(site.lastCheckAt)}>
                {site.lastCheckAt ? fmtStamp(site.lastCheckAt) : t("monitoring.never")}
              </span>

              <span className="flex min-w-0 flex-col gap-1">
                <ExpiryLine view={site.cert} kind="cert" t={t} />
                <ExpiryLine view={site.domain} kind="domain" t={t} />
              </span>

              <MonitorRowMenu
                site={site}
                canManage={canManage}
                engineOn={engineOn}
                busy={busyId === site.id}
                t={t}
                onCheck={() => onCheck(site)}
                onPause={(paused) => onPause(site, paused)}
                onEdit={() => onEdit(site)}
                onRemove={() => onRemove(site)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
